import { Injectable } from '@angular/core'
import { AngularFireDatabase } from '@angular/fire/compat/database'
import { AngularFireFunctions } from '@angular/fire/compat/functions'
import { forkJoin, Observable, of } from 'rxjs'
import { map, switchMap, tap, take, catchError } from 'rxjs/operators'
import { AttendeeModel } from '../models/attendee.model'
import { UserService } from '../services/user.service'
import { EventModel } from '@shared/models/event.model'

@Injectable({
  providedIn: 'root',
})
export class AttendeesService {
  private cache: { [key: string]: AttendeeModel[] } = {}

  constructor(
    private db: AngularFireDatabase,
    private cloudFunctions: AngularFireFunctions,
    private userService: UserService
  ) {}

  goToEvent(userId: string, eventId: string, organiserId: string, userName: string): Observable<any> {
    return this.cloudFunctions.httpsCallable('goToEvent')({
      userId,
      eventId,
      organiserId,
      userName,
    })
  }

  validate(userId: string, eventId: string, organiserId: string): Observable<any> {
    const fn = this.cloudFunctions.httpsCallable('validateAttendeeProof')
    return fn({ userId, eventId, organiserId })
  }

  cancelApplicationToEvent(userId: string, eventId: string, organiserId: string): Observable<any> {
    const fn = this.cloudFunctions.httpsCallable('eventApplicationCancelledForUser')
    return fn({ userId, eventId, organiserId })
  }

  getAllByEventId(eventId: string): Observable<Array<AttendeeModel>> {
    return this.db
      .list<any>(`/attendees/${eventId}`)
      .snapshotChanges()
      .pipe(
        map(appUserSnapshots => {
          const attendees = []
          appUserSnapshots.map(appUserSnapshot => {
            attendees.push(
              new AttendeeModel({
                name: appUserSnapshot.payload.val().name,
                email: appUserSnapshot.payload.val().email,
                id: appUserSnapshot.payload.key,
                anonymized: appUserSnapshot.payload.val().anonymized,
              })
            )
          })
          return attendees
        })
      )
  }

  getAllAttendeesByDateRange(startDate: number, endDate: number): Observable<Array<AttendeeModel>> {
    const cacheKey = `${startDate}_${endDate}`
    if (this.cache[cacheKey]) {
      return of(this.cache[cacheKey])
    }

    return this.db
      .list<{ [key: string]: EventModel }>('/events')
      .snapshotChanges()
      .pipe(
        map(eventSnapshots => {
          const events: { id: string; name: string }[] = []
          eventSnapshots.forEach(organiserSnapshot => {
            const organiserId = organiserSnapshot.key as string
            const organiserEvents = organiserSnapshot.payload.val() as { [key: string]: EventModel }
            if (organiserEvents) {
              Object.keys(organiserEvents).forEach(eventId => {
                events.push({
                  id: eventId,
                  name: organiserEvents[eventId].name,
                })
              })
            }
          })
          return events
        }),
        switchMap(events => {
          const eventMap = events.reduce((acc, event) => {
            acc[event.id] = event.name
            return acc
          }, {} as { [key: string]: string })

          if (Object.keys(eventMap).length === 0) {
            return of([])
          }

          return this.db
            .list('/attendees')
            .snapshotChanges()
            .pipe(
              map(attendeeSnapshots => attendeeSnapshots.map(snapshot => snapshot.key as string)),
              switchMap(eventIds => {
                if (eventIds.length === 0) {
                  return of([])
                }
                const attendeeObservables = eventIds.map(eventId => {
                  return this.db
                    .list<AttendeeModel>(`/attendees/${eventId}`, ref =>
                      ref.orderByChild('validatedAt').startAt(startDate).endAt(endDate)
                    )
                    .snapshotChanges()
                    .pipe(
                      take(1),
                      map(changes =>
                        changes.map(c => ({
                          id: c.payload.key,
                          eventId: eventId,
                          eventName: eventMap[eventId],
                          ...(c.payload.val() as AttendeeModel),
                        }))
                      ),
                      catchError(() => of([]))
                    )
                })
                if (attendeeObservables.length === 0) {
                  return of([])
                }
                return forkJoin(attendeeObservables).pipe(
                  map(attendeesArrays => {
                    return attendeesArrays.reduce((acc, curr) => acc.concat(curr), [])
                  }),
                  tap(flattened => {
                    this.cache[cacheKey] = flattened
                  }),
                  catchError(() => of([]))
                )
              }),
              map(flattened => flattened),
              tap(flattened => {
                this.cache[cacheKey] = flattened
              }),
              catchError(() => of([]))
            )
        }),
        catchError(() => of([]))
      )
  }

  // The statistics are cached per date range; drop them when the underlying rows change (e.g. a user was deleted).
  clearCache(): void {
    this.cache = {}
  }

  isAlreadyAppliedForEvent(userId: string, eventId: string): Observable<boolean> {
    return this.db
      .object(`/attendees/${eventId}/${userId}`)
      .valueChanges()
      .pipe(map(attendeeData => !!attendeeData))
  }
}
